import { useEffect } from 'react';

// Create a custom hook that doesn't depend on React Router context
export function useGoogleAnalytics() {
  useEffect(() => {
    // Check if GA measurement ID is available
    const measurementId = import.meta.env?.VITE_GA_MEASUREMENT_ID;
    if (!measurementId) {
      console.warn('Google Analytics: VITE_GA_MEASUREMENT_ID not found in environment variables');
      return;
    }

    // Initialize GA4 if not already done
    if (!window.gtag) {
      // Load gtag.js script dynamically
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      script.onerror = () => {
        console.error('Google Analytics: Failed to load gtag.js script');
      };
      document.head?.appendChild(script);

      // Initialize dataLayer and gtag function
      window.dataLayer = window.dataLayer || [];
      function gtag(...args) {
        window.dataLayer?.push(...args);
      }
      window.gtag = gtag;
      
      gtag('js', new Date());
      gtag('config', measurementId, {
        send_page_view: false // We'll handle page views manually
      });
    }

    // Set up a listener for route changes using browser history
    const handleRouteChange = () => {
      if (window.gtag) {
        try {
          window.gtag('event', 'page_view', {
            page_title: document?.title || '',
            page_location: window?.location?.href || '',
            page_path: window?.location?.pathname + (window?.location?.search || ''),
            custom_map: {
              'dimension1': 'user_segment',
              'dimension2': 'lead_temperature',
              'dimension3': 'age_data'
            }
          });
        } catch (error) {
          console.error('Google Analytics: Error sending page_view event:', error);
        }
      }
    };

    // Initial page view
    handleRouteChange();

    // Listen for browser navigation changes
    window.addEventListener('popstate', handleRouteChange);
    
    // Override pushState and replaceState to catch programmatic navigation
    const originalPushState = window.history?.pushState;
    const originalReplaceState = window.history?.replaceState;

    window.history.pushState = function(...args) {
      originalPushState?.apply(window.history, args);
      setTimeout(handleRouteChange, 0);
    };

    window.history.replaceState = function(...args) {
      originalReplaceState?.apply(window.history, args);
      setTimeout(handleRouteChange, 0);
    };

    // Cleanup function
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      if (originalPushState) {
        window.history.pushState = originalPushState;
      }
      if (originalReplaceState) {
        window.history.replaceState = originalReplaceState;
      }
    };
  }, []);

  return {
    isInitialized: !!window.gtag
  };
}

// Enhanced tracking functions with error handling
export const trackEvent = (eventName, parameters = {}) => {
  try {
    const measurementId = import.meta.env?.VITE_GA_MEASUREMENT_ID;
    if (window.gtag && measurementId && eventName) {
      window.gtag('event', eventName, {
        event_category: parameters?.category || 'engagement',
        event_label: parameters?.label,
        value: parameters?.value,
        custom_parameters: parameters?.custom_parameters || {},
        send_to: measurementId,
        ...parameters
      });
    }
  } catch (error) {
    console.error('Google Analytics: Error tracking event:', error);
  }
};

// Quiz-specific tracking
export const trackQuizEvent = (eventName, quizData = {}) => {
  if (!eventName) return;
  
  const { 
    currentStep, 
    totalSteps, 
    currentPhase, 
    questionId, 
    answerValue, 
    biologicalAge, 
    chronologicalAge,
    leadTemperature 
  } = quizData;

  trackEvent(eventName, {
    category: 'quiz',
    quiz_step: currentStep,
    quiz_total_steps: totalSteps,
    quiz_phase: currentPhase,
    question_id: questionId,
    answer_value: answerValue,
    biological_age: biologicalAge,
    chronological_age: chronologicalAge,
    lead_temperature: leadTemperature,
    age_difference: (biologicalAge && chronologicalAge) ? biologicalAge - chronologicalAge : null
  });
};

// VSL (Video Sales Letter) tracking
export const trackVSLEvent = (eventName, vslData = {}) => {
  if (!eventName) return;

  const { 
    videoPosition, 
    videoDuration, 
    percentageWatched, 
    milestone, 
    engagement_level 
  } = vslData;

  trackEvent(eventName, {
    category: 'vsl_engagement',
    video_position: videoPosition,
    video_duration: videoDuration,
    percentage_watched: percentageWatched,
    milestone: milestone,
    engagement_level: engagement_level
  });
};

// Calendly conversion tracking
export const trackCalendlyEvent = (eventName, calendlyData = {}) => {
  if (!eventName) return;

  const { 
    event_type, 
    booking_source, 
    user_segment, 
    lead_temperature,
    meeting_type 
  } = calendlyData;

  trackEvent(eventName, {
    category: 'conversion',
    event_type: event_type,
    booking_source: booking_source,
    user_segment: user_segment,
    lead_temperature: lead_temperature,
    meeting_type: meeting_type,
    value: 100 // Assign monetary value to conversions
  });
};

// Blog engagement tracking
export const trackBlogEvent = (eventName, blogData = {}) => {
  if (!eventName) return;

  const { 
    article_id, 
    article_title, 
    article_category, 
    read_time, 
    engagement_type,
    scroll_percentage 
  } = blogData;

  trackEvent(eventName, {
    category: 'blog_engagement',
    article_id: article_id,
    article_title: article_title,
    article_category: article_category,
    read_time: read_time,
    engagement_type: engagement_type,
    scroll_percentage: scroll_percentage
  });
};

// Newsletter signup tracking
export const trackNewsletterEvent = (eventName, newsletterData = {}) => {
  if (!eventName) return;

  const { 
    signup_source, 
    user_segment, 
    lead_magnet,
    email_provided 
  } = newsletterData;

  trackEvent(eventName, {
    category: 'lead_generation',
    signup_source: signup_source,
    user_segment: user_segment,
    lead_magnet: lead_magnet,
    email_provided: email_provided,
    value: 50 // Assign value to newsletter signups
  });
};

// Payment flow tracking
export const trackPaymentEvent = (eventName, paymentData = {}) => {
  if (!eventName) return;

  const {
    program_type, // 'efem_premium' or 'efem_starter'
    plan_type, // 'trimestral', 'semestral', 'anual', 'starter_monthly'
    amount,
    currency = 'EUR',
    payment_method,
    lead_id,
    email,
    source,
    error_message
  } = paymentData;

  trackEvent(eventName, {
    category: 'payment',
    program_type: program_type,
    plan_type: plan_type,
    amount: amount,
    currency: currency,
    payment_method: payment_method,
    lead_id: lead_id,
    email: email,
    source: source,
    error_message: error_message,
    value: amount // For conversion value tracking
  });
};

// Enhanced eCommerce tracking for purchases
export const trackPurchase = (purchaseData = {}) => {
  try {
    const {
      transaction_id,
      value,
      currency = 'EUR',
      program_type,
      plan_type,
      plan_name,
      lead_id,
      email,
      items = []
    } = purchaseData;

    if (window.gtag && transaction_id && value) {
      // Standard eCommerce purchase event
      window.gtag('event', 'purchase', {
        transaction_id: transaction_id,
        value: value,
        currency: currency,
        items: items?.map(item => ({
          item_id: item?.id,
          item_name: item?.name,
          item_category: item?.category,
          quantity: item?.quantity || 1,
          price: item?.price
        })) || [],
        // Custom parameters for EFEM tracking
        program_type: program_type,
        plan_type: plan_type,
        plan_name: plan_name,
        lead_id: lead_id,
        email: email
      });

      // Also track as a custom conversion event
      trackEvent('payment_completed', {
        category: 'conversion',
        program_type: program_type,
        plan_type: plan_type,
        amount: value,
        lead_id: lead_id,
        transaction_id: transaction_id,
        value: value
      });
    }
  } catch (error) {
    console.error('Google Analytics: Error tracking purchase:', error);
  }
};

// Subscription lifecycle tracking
export const trackSubscriptionEvent = (eventName, subscriptionData = {}) => {
  if (!eventName) return;

  const {
    subscription_id,
    plan_type,
    plan_name,
    amount,
    currency = 'EUR',
    status, // 'active', 'cancelled', 'past_due', 'expired'
    period_start,
    period_end,
    lead_id,
    email,
    cancellation_reason,
    renewal_count
  } = subscriptionData;

  trackEvent(eventName, {
    category: 'subscription',
    subscription_id: subscription_id,
    plan_type: plan_type,
    plan_name: plan_name,
    amount: amount,
    currency: currency,
    status: status,
    period_start: period_start,
    period_end: period_end,
    lead_id: lead_id,
    email: email,
    cancellation_reason: cancellation_reason,
    renewal_count: renewal_count,
    value: eventName === 'subscription_renewed' ? amount : 0
  });
};

// User identification for enhanced tracking
export const identifyUser = (userData = {}) => {
  try {
    const measurementId = import.meta.env?.VITE_GA_MEASUREMENT_ID;
    const { 
      user_id, 
      age_range, 
      lead_temperature, 
      signup_date,
      segment 
    } = userData;

    if (window.gtag && measurementId) {
      window.gtag('config', measurementId, {
        custom_map: {
          'dimension1': segment,
          'dimension2': lead_temperature,
          'dimension3': age_range
        },
        user_id: user_id
      });
    }
  } catch (error) {
    console.error('Google Analytics: Error identifying user:', error);
  }
};

export default useGoogleAnalytics;